import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import SubHeaders from '@/components/Headers/SubHeaders'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getDashboardStats } from '../../services'
import {
    Package,
    Warehouse,
    Box,
    TrendingUp,
    Users,
    ArrowDownToLine,
    ArrowUpFromLine,
    ArrowRightLeft,
    Layers,
} from 'lucide-react'
import { paths } from '@/utils/path'
import useTabs from '@/hooks/useTabs'
import { Tabs } from 'vnetwork-ui'
import EChart from '@/components/EchartCustomized/MapEchart'
import { SCHEMES } from '@/components/EchartCustomized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useNavigate } from 'react-router-dom'

interface DashboardProps {
    /** Hide header and tabs when embedded in Overview component */
    hideHeader?: boolean
}

const DashboardSkeleton = ({ hideHeader }: DashboardProps) => (
    <div>
        {!hideHeader && (
            <>
                <SubHeaders
                    breadcrumbs={[
                        {
                            path: paths.warehouse,
                            title: 'Capacities & Resources',
                        },
                    ]}
                />

                <div className="lg:flex lg:flex-wrap items-center gap-4 mt-1 mb-2 bg-white rounded-md justify-between">
                    <Skeleton className="h-10 w-48" />
                </div>
            </>
        )}

        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-2 mt-2">
            {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="lg:col-span-2 col-span-12">
                    <CardHeader className="pb-2">
                        <Skeleton className="h-4 w-24" />
                    </CardHeader>
                    <CardContent>
                        <Skeleton className="h-10 w-16 mb-2" />
                        <Skeleton className="h-3 w-20" />
                    </CardContent>
                </Card>
            ))}
        </div>

        {/* Charts Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 mt-2">
            <Card className="lg:col-span-6 col-span-12">
                <CardHeader>
                    <Skeleton className="h-6 w-48" />
                </CardHeader>
                <CardContent>
                    <Skeleton className="h-[300px] w-full" />
                </CardContent>
            </Card>
            <Card className="lg:col-span-6 col-span-12">
                <CardHeader>
                    <Skeleton className="h-6 w-48" />
                </CardHeader>
                <CardContent>
                    <Skeleton className="h-[300px] w-full" />
                </CardContent>
            </Card>
        </div>
    </div>
)

const WarehouseDashboard = ({ hideHeader = false }: DashboardProps) => {
    const navigate = useNavigate()

    // Setup tabs
    const { TabProps } = useTabs({
        tabs: [
            {
                label: 'Dashboard',
                value: 'dashboard',
                component: <div />,
            },
            {
                label: 'Statistics',
                value: 'statistics',
                component: <div />,
            },
        ],
    })

    const { data: stats, renderState } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.WAREHOUSE_DASHBOARD],
            queryFn: getDashboardStats,
            staleTime: 5 * 60 * 1000, // 5 minutes
        },
        {
            loadingComponent: <DashboardSkeleton hideHeader={hideHeader} />,
        }
    )

    // Helper to convert Record to array
    const recordToChartData = (
        record: Record<string, number>,
        colors: string[]
    ) => {
        return Object.entries(record).map(([key, value], index) => ({
            name: key
                .replace(/_/g, ' ')
                .replace(/\b\w/g, (l) => l.toUpperCase()),
            value,
            fill: colors[index % colors.length],
        }))
    }

    const statusColors = [
        SCHEMES.success[0], // green
        SCHEMES.info[0], // blue
        SCHEMES.brand[5], // purple
        SCHEMES.warning[0], // yellow
        SCHEMES.error[0], // red
    ]

    const categoryColors = SCHEMES.default

    // Render loading/error state at the end (after all hooks)
    if (renderState()) return renderState()

    if (!stats) return null

    // Prepare chart data (with fallback for empty data)
    const equipmentChartData = recordToChartData(
        stats.equipment_by_status || {},
        statusColors
    )
    const componentChartData = recordToChartData(
        stats.components_by_status || {},
        statusColors
    )
    const locationChartData = recordToChartData(
        stats.equipment_by_location || {},
        categoryColors
    )
    const equipmentCategoryData = recordToChartData(
        stats.equipment_by_category || {},
        categoryColors
    )
    const componentCategoryData = recordToChartData(
        stats.components_by_category || {},
        categoryColors
    )
    const supplierChartData = recordToChartData(
        stats.equipment_by_supplier || {},
        categoryColors
    )

    // Age distribution chart data
    const ageDistributionData = [
        {
            name: '< 1 Year',
            value: stats.equipment_age_distribution.less_than_1_year,
        },
        {
            name: '1-3 Years',
            value: stats.equipment_age_distribution.one_to_three_years,
        },
        {
            name: '3-5 Years',
            value: stats.equipment_age_distribution.three_to_five_years,
        },
        {
            name: '5+ Years',
            value: stats.equipment_age_distribution.more_than_5_years,
        },
        {
            name: 'No Date',
            value: stats.equipment_age_distribution.no_date_info,
        },
    ]

    // Format month from YYYY-MM to short format (e.g., "Jan 25")
    const formatMonth = (monthStr: string) => {
        const [year, month] = monthStr.split('-')
        const date = new Date(parseInt(year), parseInt(month) - 1)
        const monthName = date.toLocaleDateString('en-US', { month: 'short' })
        const shortYear = year.slice(2) // Get last 2 digits of year
        return `${monthName} ${shortYear}`
    }

    // Process trend data - format for line chart (limit to last 6 months)
    const processTrendData = (stats.process_trend_monthly || [])
        .sort((a, b) => a.month.localeCompare(b.month)) // Sort chronologically by YYYY-MM
        .slice(-6) // Only take last 6 months
        .map((trend) => ({
            month: formatMonth(trend.month),
            Import: trend.import,
            Export: trend.export,
            Transfer: trend.transfer,
        }))

    // Format currency
    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND',
        }).format(value)
    }

    return (
        <div>
            {!hideHeader && (
                <>
                    {/* SubHeaders like tickets page */}
                    <SubHeaders
                        breadcrumbs={[
                            {
                                path: paths.warehouse,
                                title: 'Capacities & Resources',
                            },
                        ]}
                    />

                    {/* Tabs and actions container like tickets page */}
                    <div className="lg:flex lg:flex-wrap items-center gap-4 mb-2 bg-white rounded-md justify-between">
                        <div className="flex items-center gap-2">
                            <Tabs {...TabProps} tabContainerClassName="pt-0" />
                        </div>

                        <div className="flex items-center gap-2 justify-end">
                            {/* Action buttons can be added here later */}
                        </div>
                    </div>
                </>
            )}

            {/* Stats Cards - using lg:grid-cols-12 like tickets */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-2 mt-2">
                <Card
                    className="lg:col-span-2 md:col-span-6 col-span-12 hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(paths.warehouseLocations)}
                >
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">
                            Total Locations
                        </CardTitle>
                        <Warehouse className="h-5 w-5 text-blue-600" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-3xl font-bold">
                            {stats.total_locations}
                        </div>
                        <CardDescription className="mt-1">
                            Warehouses & Datacenters
                        </CardDescription>
                    </CardContent>
                </Card>

                <Card
                    className="lg:col-span-2 md:col-span-6 col-span-12 hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() =>
                        navigate(`${paths.warehouseItems}?tab=server`)
                    }
                >
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">
                            Total Equipment
                        </CardTitle>
                        <Package className="h-5 w-5 text-green-600" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-3xl font-bold">
                            {stats.total_equipment}
                        </div>
                        <CardDescription className="mt-1">
                            All equipment items
                        </CardDescription>
                    </CardContent>
                </Card>

                <Card
                    className="lg:col-span-2 md:col-span-6 col-span-12 hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(`${paths.warehouseItems}?tab=cpu`)}
                >
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">
                            Total Components
                        </CardTitle>
                        <Box className="h-5 w-5 text-purple-600" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-3xl font-bold">
                            {stats.total_components}
                        </div>
                        <CardDescription className="mt-1">
                            All component parts
                        </CardDescription>
                    </CardContent>
                </Card>

                <Card
                    className="lg:col-span-2 md:col-span-6 col-span-12 hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(paths.warehouseProcesses)}
                >
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">
                            Total Processes
                        </CardTitle>
                        <TrendingUp className="h-5 w-5 text-orange-600" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-3xl font-bold">
                            {stats.total_processes}
                        </div>
                        <CardDescription className="mt-1">
                            Import/Export/Transfer
                        </CardDescription>
                    </CardContent>
                </Card>

                <Card
                    className="lg:col-span-2 md:col-span-6 col-span-12 hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(paths.warehouseSuppliers)}
                >
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">
                            Total Suppliers
                        </CardTitle>
                        <Users className="h-5 w-5 text-pink-600" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-3xl font-bold">
                            {stats.total_suppliers}
                        </div>
                        <CardDescription className="mt-1">
                            Active suppliers
                        </CardDescription>
                    </CardContent>
                </Card>

                <Card
                    className="lg:col-span-2 md:col-span-6 col-span-12 hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(paths.warehouseModels)}
                >
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">
                            Item Models
                        </CardTitle>
                        <Layers className="h-5 w-5 text-indigo-600" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-3xl font-bold">
                            {stats.total_models || 0}
                        </div>
                        <CardDescription className="mt-1">
                            Model templates
                        </CardDescription>
                    </CardContent>
                </Card>
            </div>

            {/* Charts - using lg:grid-cols-12 like tickets */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 mt-2">
                {/* Equipment Status Chart */}
                <Card className="lg:col-span-6 col-span-12">
                    <CardHeader>
                        <CardTitle>Equipment by Status</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <EChart
                            options={{
                                tooltip: {
                                    trigger: 'axis',
                                    axisPointer: {
                                        type: 'shadow',
                                    },
                                },
                                legend: {
                                    bottom: 0,
                                    itemWidth: 8,
                                    itemHeight: 8,
                                },
                                xAxis: {
                                    type: 'category',
                                    data: equipmentChartData.map(
                                        (item) => item.name
                                    ),
                                },
                                yAxis: {
                                    type: 'value',
                                },
                                series: [
                                    {
                                        type: 'bar',
                                        data: equipmentChartData.map(
                                            (item) => ({
                                                value: item.value,
                                                itemStyle: {
                                                    color: item.fill,
                                                    borderRadius: [8, 8, 0, 0],
                                                },
                                            })
                                        ),
                                        label: {
                                            show: true,
                                            position: 'top',
                                            fontWeight: 600,
                                        },
                                    },
                                ],
                                grid: {
                                    left: '0%',
                                    right: '0%',
                                    bottom: '15%',
                                    containLabel: true,
                                    top: '10%',
                                },
                            }}
                        />
                    </CardContent>
                </Card>

                {/* Component Status Chart */}
                <Card className="lg:col-span-6 col-span-12">
                    <CardHeader>
                        <CardTitle>Components by Status</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <EChart
                            options={{
                                tooltip: {
                                    trigger: 'axis',
                                    axisPointer: {
                                        type: 'shadow',
                                    },
                                },
                                legend: {
                                    bottom: 0,
                                    itemWidth: 8,
                                    itemHeight: 8,
                                },
                                xAxis: {
                                    type: 'category',
                                    data: componentChartData.map(
                                        (item) => item.name
                                    ),
                                },
                                yAxis: {
                                    type: 'value',
                                },
                                series: [
                                    {
                                        type: 'bar',
                                        data: componentChartData.map(
                                            (item) => ({
                                                value: item.value,
                                                itemStyle: {
                                                    color: item.fill,
                                                    borderRadius: [8, 8, 0, 0],
                                                },
                                            })
                                        ),
                                        label: {
                                            show: true,
                                            position: 'top',
                                            fontWeight: 600,
                                        },
                                    },
                                ],
                                grid: {
                                    left: '0%',
                                    right: '0%',
                                    bottom: '15%',
                                    containLabel: true,
                                    top: '10%',
                                },
                            }}
                        />
                    </CardContent>
                </Card>
            </div>

            {/* Phase 1: Location & Category Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 mt-2">
                {/* Equipment Distribution by Location */}
                <Card className="lg:col-span-12 col-span-12">
                    <CardHeader>
                        <CardTitle>
                            Equipment Distribution by Location
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <EChart
                            options={{
                                tooltip: {
                                    trigger: 'axis',
                                    axisPointer: { type: 'shadow' },
                                },
                                xAxis: {
                                    type: 'category',
                                    data: locationChartData.map(
                                        (item) => item.name
                                    ),
                                },
                                yAxis: { type: 'value' },
                                series: [
                                    {
                                        type: 'bar',
                                        data: locationChartData.map((item) => ({
                                            value: item.value,
                                            itemStyle: {
                                                color: item.fill,
                                                borderRadius: [8, 8, 0, 0],
                                            },
                                        })),
                                        label: {
                                            show: true,
                                            position: 'top',
                                            fontWeight: 600,
                                        },
                                    },
                                ],
                                grid: {
                                    left: '0%',
                                    right: '0%',
                                    bottom: '20%',
                                    containLabel: true,
                                    top: '10%',
                                },
                            }}
                        />
                    </CardContent>
                </Card>
            </div>

            {/* Category Breakdown Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 mt-2">
                {/* Equipment by Category */}
                <Card className="lg:col-span-6 col-span-12">
                    <CardHeader>
                        <CardTitle>Equipment by Category</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <EChart
                            options={{
                                tooltip: { trigger: 'item' },
                                legend: {
                                    bottom: 0,
                                    itemWidth: 8,
                                    itemHeight: 8,
                                },
                                series: [
                                    {
                                        type: 'pie',
                                        radius: '60%',
                                        data: equipmentCategoryData.map(
                                            (item) => ({
                                                name: item.name,
                                                value: item.value,
                                            })
                                        ),
                                        label: { show: true },
                                    },
                                ],
                            }}
                        />
                    </CardContent>
                </Card>

                {/* Components by Category */}
                <Card className="lg:col-span-6 col-span-12">
                    <CardHeader>
                        <CardTitle>Components by Category</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <EChart
                            options={{
                                tooltip: { trigger: 'item' },
                                legend: {
                                    bottom: 0,
                                    itemWidth: 8,
                                    itemHeight: 8,
                                },
                                series: [
                                    {
                                        type: 'pie',
                                        radius: '60%',
                                        data: componentCategoryData.map(
                                            (item) => ({
                                                name: item.name,
                                                value: item.value,
                                            })
                                        ),
                                        label: { show: true },
                                    },
                                ],
                            }}
                        />
                    </CardContent>
                </Card>
            </div>

            {/* Phase 2: Financial & Warranty */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 mt-2">
                {/* Financial Value */}
                <Card className="lg:col-span-4 col-span-12">
                    <CardHeader>
                        <CardTitle>Inventory Value</CardTitle>
                        <CardDescription>Total asset value</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="text-3xl font-bold text-green-600">
                            {formatCurrency(stats.total_inventory_value)}
                        </div>
                    </CardContent>
                </Card>

                {/* Warranty Alerts */}
                <Card className="lg:col-span-8 col-span-12">
                    <CardHeader>
                        <CardTitle>Warranty Expiration Alerts</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                            <div className="text-center p-2 bg-yellow-50 rounded-md">
                                <div className="text-2xl font-bold text-yellow-600">
                                    {stats.warranty_expiring.expiring_30_days}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    30 Days
                                </div>
                            </div>
                            <div className="text-center p-2 bg-orange-50 rounded-md">
                                <div className="text-2xl font-bold text-orange-600">
                                    {stats.warranty_expiring.expiring_60_days}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    60 Days
                                </div>
                            </div>
                            <div className="text-center p-2 bg-amber-50 rounded-md">
                                <div className="text-2xl font-bold text-amber-600">
                                    {stats.warranty_expiring.expiring_90_days}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    90 Days
                                </div>
                            </div>
                            <div className="text-center p-2 bg-red-50 rounded-md">
                                <div className="text-2xl font-bold text-red-600">
                                    {stats.warranty_expiring.already_expired}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    Expired
                                </div>
                            </div>
                            <div className="text-center p-2 bg-gray-50 rounded-md">
                                <div className="text-2xl font-bold text-gray-600">
                                    {stats.warranty_expiring.no_warranty_info}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    No Info
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Supplier Analytics */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 mt-2">
                <Card className="lg:col-span-12 col-span-12">
                    <CardHeader>
                        <CardTitle>Top Suppliers by Equipment Count</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <EChart
                            options={{
                                tooltip: {
                                    trigger: 'axis',
                                    axisPointer: { type: 'shadow' },
                                },
                                xAxis: {
                                    type: 'category',
                                    data: supplierChartData.map(
                                        (item) => item.name
                                    ),
                                },
                                yAxis: { type: 'value' },
                                series: [
                                    {
                                        type: 'bar',
                                        data: supplierChartData.map((item) => ({
                                            value: item.value,
                                            itemStyle: {
                                                color: item.fill,
                                                borderRadius: [8, 8, 0, 0],
                                            },
                                        })),
                                        label: {
                                            show: true,
                                            position: 'top',
                                            fontWeight: 600,
                                        },
                                    },
                                ],
                                grid: {
                                    left: '0%',
                                    right: '0%',
                                    bottom: '20%',
                                    containLabel: true,
                                    top: '10%',
                                },
                            }}
                        />
                    </CardContent>
                </Card>
            </div>

            {/* Phase 3: Process Trends & Equipment Age */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 mt-2">
                {/* Process Trends Monthly */}
                <Card className="lg:col-span-7 col-span-12">
                    <CardHeader>
                        <CardTitle>Process Trends (Last 6 Months)</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <EChart
                            options={{
                                tooltip: {
                                    trigger: 'axis',
                                    axisPointer: {
                                        type: 'shadow',
                                    },
                                },
                                legend: {
                                    bottom: 0,
                                    data: ['Import', 'Export', 'Transfer'],
                                    itemWidth: 8,
                                    itemHeight: 8,
                                },
                                xAxis: {
                                    type: 'category',
                                    data: processTrendData.map((d) => d.month),
                                    axisLabel: {
                                        rotate: 45,
                                        interval: 0,
                                    },
                                },
                                yAxis: { type: 'value' },
                                series: [
                                    {
                                        name: 'Import',
                                        type: 'bar',
                                        stack: 'total',
                                        data: processTrendData.map(
                                            (d) => d.Import
                                        ),
                                        itemStyle: {
                                            color: SCHEMES.info[0],
                                        },
                                    },
                                    {
                                        name: 'Export',
                                        type: 'bar',
                                        stack: 'total',
                                        data: processTrendData.map(
                                            (d) => d.Export
                                        ),
                                        itemStyle: {
                                            color: SCHEMES.error[0],
                                        },
                                    },
                                    {
                                        name: 'Transfer',
                                        type: 'bar',
                                        stack: 'total',
                                        data: processTrendData.map(
                                            (d) => d.Transfer
                                        ),
                                        itemStyle: {
                                            color: SCHEMES.success[0],
                                        },
                                    },
                                ],
                                grid: {
                                    left: '0%',
                                    right: '0%',
                                    bottom: '25%',
                                    containLabel: true,
                                    top: '10%',
                                },
                            }}
                        />
                    </CardContent>
                </Card>

                {/* Equipment Age Distribution */}
                <Card className="lg:col-span-5 col-span-12">
                    <CardHeader>
                        <CardTitle>Equipment Age Distribution</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <EChart
                            options={{
                                tooltip: { trigger: 'item' },
                                legend: {
                                    bottom: 0,
                                    itemWidth: 8,
                                    itemHeight: 8,
                                },
                                series: [
                                    {
                                        type: 'pie',
                                        radius: '60%',
                                        data: ageDistributionData.map(
                                            (item, index) => ({
                                                name: item.name,
                                                value: item.value,
                                                itemStyle: {
                                                    color: statusColors[
                                                        index %
                                                            statusColors.length
                                                    ],
                                                },
                                            })
                                        ),
                                        label: { show: true },
                                    },
                                ],
                            }}
                        />
                    </CardContent>
                </Card>
            </div>

            {/* Recent Process Activity */}
            <Card className="mt-2">
                <CardHeader>
                    <CardTitle>Recent Process Activity</CardTitle>
                    <CardDescription>
                        Last 10 warehouse operations
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-1">
                        {(stats.recent_processes || []).length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                No recent processes
                            </div>
                        ) : (
                            (stats.recent_processes || []).map((process) => {
                                const processIcon =
                                    process.type === 'import'
                                        ? ArrowDownToLine
                                        : process.type === 'export'
                                          ? ArrowUpFromLine
                                          : ArrowRightLeft
                                const ProcessIcon = processIcon

                                const statusColor =
                                    process.status === 'completed'
                                        ? 'bg-green-100 text-green-700 border-green-200'
                                        : process.status === 'pending'
                                          ? 'bg-yellow-100 text-yellow-700 border-yellow-200'
                                          : process.status === 'processing'
                                            ? 'bg-blue-100 text-blue-700 border-blue-200'
                                            : 'bg-gray-100 text-gray-700 border-gray-200'

                                const formatDateTime = (dateStr: string) => {
                                    const date = new Date(dateStr)
                                    return date.toLocaleString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                    })
                                }

                                return (
                                    <div
                                        key={process.id}
                                        className="flex items-center justify-between py-2 px-3 hover:bg-muted/50 rounded-md transition border-b last:border-b-0 cursor-pointer"
                                        onClick={() =>
                                            navigate(
                                                `${paths.warehouseProcesses}/${process.id}`
                                            )
                                        }
                                    >
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                            {/* Process Icon */}
                                            <div
                                                className={`flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center ${
                                                    process.type === 'import'
                                                        ? 'bg-blue-100 text-blue-600'
                                                        : process.type ===
                                                            'export'
                                                          ? 'bg-red-100 text-red-600'
                                                          : 'bg-purple-100 text-purple-600'
                                                }`}
                                            >
                                                <ProcessIcon className="w-4 h-4" />
                                            </div>

                                            {/* Process Info */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-0.5">
                                                    <span className="font-medium text-sm capitalize">
                                                        {process.type.replace(
                                                            '_',
                                                            ' '
                                                        )}
                                                    </span>
                                                    <span className="text-xs text-muted-foreground font-mono">
                                                        {process.code}
                                                    </span>
                                                    <span
                                                        className={`text-xs px-2 py-0.5 rounded-full border ${statusColor}`}
                                                    >
                                                        {process.status}
                                                    </span>
                                                </div>
                                                <div className="text-xs text-muted-foreground truncate">
                                                    {process.reason}
                                                </div>
                                                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
                                                    <span className="truncate">
                                                        {process.from_location ||
                                                            'N/A'}
                                                    </span>
                                                    <span>→</span>
                                                    <span className="truncate">
                                                        {process.to_location ||
                                                            'N/A'}
                                                    </span>
                                                </div>
                                                <div className="text-xs text-muted-foreground mt-0.5">
                                                    {process.item_summary}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Timestamp and ID */}
                                        <div className="flex-shrink-0 text-right ml-3">
                                            <div className="text-xs text-muted-foreground">
                                                {formatDateTime(
                                                    process.created_at
                                                )}
                                            </div>
                                            <div className="text-xs text-muted-foreground/70 font-mono">
                                                {process.code}
                                            </div>
                                        </div>
                                    </div>
                                )
                            })
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    )
}

export default WarehouseDashboard
